import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, api } from '../api/client'
import type {
  BrainDecision,
  BrainModuleInfo,
  BrainProposal,
  BrainProposalStatus,
  BrainTraceEvent,
  HoldingWord,
  IdeaWord,
  MarketMode,
  ModuleMode,
} from '../api/types'
import { Empty, ErrorBox, Loading } from '../components/Loading'
import Modal from '../components/Modal'
import { HoldingTracker } from '../components/HoldingTracker'
import { BrainApprovalsCard, BrainCompareCard, BrainStageCard } from '../components/BrainGoLive'
import { formatCurrency, formatDateTime } from '../lib/format'
import { MODULE_PLAIN, moduleName, traceLine } from '../trademind/vocab'
import { runNowLine, useBrainRunNow } from '../trademind/live'
import { SEALED_PROPOSAL_NOTE } from '../trademind/live-system'

/* The brain console (build book module M17). Everything here is in plain
 * words: the brain only suggests and records; it never places an order. */

const STEP_LABEL: Record<string, string> = {
  perceive: '1 · Look at the data',
  state: '2 · Read the market',
  recognise: '3 · Recognise the situation',
  remember: '4 · Remember similar times',
  reason: '5 · Form an opinion',
  risk: '6 · Safety check',
  decide: '7 · Decide',
  learn: '8 · Learn from results',
}

const MODE_LABEL: Record<ModuleMode, string> = { on: 'On', shadow: 'Trial', off: 'Off' }

const ROTATION: Record<string, string> = {
  leading: 'Leading — stronger for 1 and 3 months',
  improving: 'Improving — stronger this month',
  weakening: 'Weakening — slipping this month',
  lagging: 'Lagging — weaker for 1 and 3 months',
}

const MARKET: Record<MarketMode, { label: string; className: string; plain: string }> = {
  NORMAL: { label: 'NORMAL', className: 'banner-ok', plain: 'New ideas are allowed at full size.' },
  DEFENSIVE: {
    label: 'DEFENSIVE',
    className: 'banner-warn',
    plain: 'Careful market: at most two new ideas, at half size.',
  },
  NO_NEW_TRADES: {
    label: 'NO NEW TRADES',
    className: 'banner-warn',
    plain: 'No new buys today. Stocks you hold are still watched and sold as usual.',
  },
}

const WORD_BADGE: Record<string, string> = {
  TRADE: 'badge-buy',
  WATCH: 'badge-watch',
  WAIT: 'badge-hold',
  AVOID: 'badge-sell',
  HOLD: 'badge-on',
  MONITOR: 'badge-watch',
  REDUCE: 'badge-warn',
  EXIT: 'badge-exit',
}

const WORD_PLAIN: Record<string, string> = {
  TRADE: 'A planned buy with a size, target and stop',
  WATCH: 'Interesting, but something is blocking it',
  WAIT: 'No reason to act right now',
  AVOID: 'Stay away for a stated reason',
  HOLD: 'Keep it; the plan is working',
  MONITOR: 'Keep it, but watch closely',
  REDUCE: 'Take some off',
  EXIT: 'Leave the position',
}

// Most careful first, the same order the brain uses.
const IDEA_ORDER: IdeaWord[] = ['AVOID', 'WAIT', 'WATCH', 'TRADE']
const HOLDING_ORDER: HoldingWord[] = ['EXIT', 'REDUCE', 'MONITOR', 'HOLD']

function finalWord(d: BrainDecision): string {
  return d.overruled_word ?? d.word
}

function moreCareful(d: BrainDecision): string[] {
  const order: string[] = d.kind === 'idea' ? IDEA_ORDER : HOLDING_ORDER
  return order.slice(0, order.indexOf(finalWord(d)))
}

function WordPill({ word }: { word: string }) {
  return (
    <span className={`badge ${WORD_BADGE[word] ?? 'badge-off'}`} title={WORD_PLAIN[word]}>
      {word}
    </span>
  )
}

function HowItDecided({ trace }: { trace: BrainTraceEvent[] }) {
  const steps = Array.from(new Set(trace.map((e) => e.step)))
  return (
    <ol className="brain-steps">
      {steps.map((step) => (
        <li key={step}>
          <strong>{STEP_LABEL[step] ?? step}</strong>
          <ul>
            {trace
              .filter((e) => e.step === step)
              .map((e, i) => (
                <li key={i} className="muted">
                  {traceLine(e)}
                </li>
              ))}
          </ul>
        </li>
      ))}
    </ol>
  )
}

/** The plan as plain text, for pasting into a broker app or a note. Copies
 * text only — the brain never places an order. */
function planText(d: BrainDecision): string {
  const zone =
    d.entry_low != null && d.entry_high != null
      ? `${formatCurrency(d.entry_low)} to ${formatCurrency(d.entry_high)}`
      : '—'
  return [
    `${d.symbol}: TRADE`,
    `Buy around ${zone}`,
    `Target ${d.target != null ? formatCurrency(d.target) : '—'} (+8%)`,
    `Stop ${d.stop != null ? formatCurrency(d.stop) : '—'} (−4%)`,
    `Shares ${d.qty} · up to ${d.horizon_days} trading days`,
  ].join('\n')
}

function CopyPlan({ decision }: { decision: BrainDecision }) {
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle')
  return (
    <div style={{ marginTop: '0.6rem' }}>
      <button
        onClick={() => {
          navigator.clipboard
            .writeText(planText(decision))
            .then(() => setCopied('done'))
            .catch(() => setCopied('failed'))
        }}
      >
        Copy plan
      </button>
      {copied === 'done' && <span className="stat-sub"> Copied.</span>}
      {copied === 'failed' && <span className="stat-sub"> Could not copy; select the levels above instead.</span>}
    </div>
  )
}

function AlertCard({ runId }: { runId: string }) {
  const preview = useQuery({ queryKey: ['brainAlert', runId], queryFn: () => api.brainAlertPreview(runId) })
  const send = useMutation({ mutationFn: () => api.brainAlertSend(runId), onSuccess: () => void preview.refetch() })
  const items = preview.data?.items ?? []
  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div className="between">
        <h2>Telegram alert for this run</h2>
        <button className="primary" disabled={!items.length || send.isPending} onClick={() => send.mutate()}>
          {send.isPending ? 'Sending…' : 'Send now'}
        </button>
      </div>
      {preview.isLoading && <Loading />}
      {!preview.isLoading && items.length === 0 && (
        <p className="muted">Nothing new to tell since the last run (or it was already sent today).</p>
      )}
      {items.length > 0 && (
        <ul className="brain-reasons">
          {items.map((i) => (
            <li key={i.key}>{i.text}</li>
          ))}
        </ul>
      )}
      {send.data && (
        <p className="stat-sub">
          {send.data.sent
            ? `Sent ${send.data.count} update${send.data.count === 1 ? '' : 's'} to Telegram.`
            : send.data.count
              ? 'Not sent: Telegram is switched off or did not answer. Nothing was marked as told.'
              : 'Nothing new to send.'}
        </p>
      )}
      {send.isError && <ErrorBox error={send.error} />}
    </div>
  )
}

function DecisionDetail({
  decision,
  trace,
  onOverruled,
}: {
  decision: BrainDecision
  trace: BrainTraceEvent[] | undefined
  onOverruled: () => void
}) {
  const options = moreCareful(decision)
  const [word, setWord] = useState(options[options.length - 1] ?? '')
  const [reason, setReason] = useState('')
  const overrule = useMutation({
    mutationFn: () => api.brainOverrule(decision.id, word, reason),
    onSuccess: onOverruled,
  })

  return (
    <>
      <div className="between">
        <h2 style={{ margin: 0 }}>{decision.symbol}</h2>
        <WordPill word={finalWord(decision)} />
      </div>
      <p className="muted" style={{ marginTop: '0.3rem' }}>
        {WORD_PLAIN[finalWord(decision)]}
      </p>

      {decision.kind === 'holding' && <HoldingTracker symbol={decision.symbol} />}

      {decision.overruled_word && (
        <div className="banner banner-info" style={{ margin: '0.6rem 0' }}>
          You changed this from {decision.word} to {decision.overruled_word}: “{decision.overrule_reason}”
        </div>
      )}

      {finalWord(decision) === 'TRADE' && decision.entry_low != null && (
        <div className="grid brain-levels">
          <div>
            <div className="stat-sub">Buy around</div>
            <strong>{formatCurrency(decision.entry_low)}</strong>
          </div>
          <div>
            <div className="stat-sub">Target (+8%)</div>
            <strong>{decision.target != null ? formatCurrency(decision.target) : '—'}</strong>
          </div>
          <div>
            <div className="stat-sub">Stop (−4%)</div>
            <strong>{decision.stop != null ? formatCurrency(decision.stop) : '—'}</strong>
          </div>
          <div>
            <div className="stat-sub">Shares</div>
            <strong>{decision.qty}</strong>
          </div>
        </div>
      )}

      {finalWord(decision) === 'TRADE' && <CopyPlan decision={decision} />}

      <h3>Why</h3>
      <ul className="brain-reasons">
        {decision.reasons.map((r, i) => (
          <li key={i}>{r}</li>
        ))}
      </ul>

      {decision.evidence_text && (
        <>
          <h3>Evidence</h3>
          <p className="muted">{decision.evidence_text}</p>
        </>
      )}

      <h3>How the brain decided</h3>
      {trace ? <HowItDecided trace={trace} /> : <Loading />}

      {options.length > 0 && (
        <>
          <h3>Overrule</h3>
          <p className="stat-sub">You can only make the brain more careful, and you must say why.</p>
          <div className="brain-overrule">
            <select value={word} onChange={(e) => setWord(e.target.value)} aria-label="New decision">
              {options.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why? e.g. results next week"
              aria-label="Reason"
            />
            <button
              className="primary"
              disabled={!reason.trim() || overrule.isPending}
              onClick={() => overrule.mutate()}
            >
              {overrule.isPending ? 'Saving…' : 'Overrule'}
            </button>
          </div>
          {overrule.isError && <ErrorBox error={overrule.error} />}
        </>
      )}
    </>
  )
}

function DecisionRow({ d, onOpen }: { d: BrainDecision; onOpen: () => void }) {
  return (
    <tr onClick={onOpen} style={{ cursor: 'pointer' }}>
      <td>
        <strong>{d.symbol}</strong>
      </td>
      <td>
        <WordPill word={finalWord(d)} />
        {d.overruled_word && <div className="stat-sub">brain said {d.word}</div>}
      </td>
      <td className="brain-reason-cell">
        {d.reasons[0]}
        {finalWord(d) === 'TRADE' && d.entry_low != null && (
          <div className="stat-sub">
            {d.qty} shares near {formatCurrency(d.entry_low)} · target{' '}
            {d.target != null ? formatCurrency(d.target) : '—'} · stop {d.stop != null ? formatCurrency(d.stop) : '—'}
          </div>
        )}
      </td>
      <td>
        <button onClick={(e) => (e.stopPropagation(), onOpen())}>Why?</button>
      </td>
    </tr>
  )
}

function ModuleSwitch({ m, onChange }: { m: BrainModuleInfo; onChange: (mode: ModuleMode) => void }) {
  if (m.mandatory) {
    return <span className="badge badge-on" title="The safety check can never be switched off">Always on</span>
  }
  return (
    <div className="brain-switch" role="group" aria-label={`${m.name} mode`}>
      {(['on', 'shadow', 'off'] as ModuleMode[]).map((mode) => (
        <button key={mode} className={m.mode === mode ? 'primary' : ''} onClick={() => onChange(mode)}>
          {MODE_LABEL[mode]}
        </button>
      ))}
    </div>
  )
}

const PROPOSAL_STATUS_LABEL: Record<BrainProposalStatus, string> = {
  open: 'Open',
  accepted: 'Accepted',
  dismissed: 'Rejected',
}

const PROPOSAL_STATUS_BADGE: Record<BrainProposalStatus, string> = {
  open: 'badge-watch',
  accepted: 'badge-buy',
  dismissed: 'badge-sell',
}

/** The buy_level proposal actually in force right now (if any): the most
 * recently decided accepted one, when it did not revert back to the
 * default. Mirrors store.accepted_buy_level's own ordering. */
function currentBuyLevelProposal(proposals: BrainProposal[]): BrainProposal | null {
  const accepted = proposals.filter((p) => p.kind === 'buy_level' && p.status === 'accepted')
  if (accepted.length === 0) return null
  const latest = accepted.reduce((a, b) => ((a.decided_at ?? '') > (b.decided_at ?? '') ? a : b))
  // A sealed one (blind trial) hides its value, but the owner's "back to default" is never sealed.
  return latest.sealed || latest.change.buy_level != null ? latest : null
}

function ProposalRow({
  p,
  pending,
  onDecide,
}: {
  p: BrainProposal
  pending: boolean
  onDecide: (id: number, action: 'accept' | 'reject', note: string) => void
}) {
  const [note, setNote] = useState('')
  return (
    <tr>
      <td>
        <strong>{p.title}</strong>
        <div className="stat-sub">{p.evidence}</div>
      </td>
      <td>
        <span className={`badge ${PROPOSAL_STATUS_BADGE[p.status]}`}>{PROPOSAL_STATUS_LABEL[p.status]}</span>
        {p.status !== 'open' && (
          <div className="stat-sub">
            {p.decided_by ? `by ${p.decided_by}` : ''}
            {p.decided_at ? ` on ${formatDateTime(p.decided_at)}` : ''}
            {p.decided_note ? ` — “${p.decided_note}”` : ''}
          </div>
        )}
      </td>
      <td>
        {/* Made during the blind trial: sealed by the server, so it cannot be judged here yet. */}
        {p.sealed && p.status === 'open' && <div className="stat-sub">{SEALED_PROPOSAL_NOTE}</div>}
        {!p.sealed && p.status === 'open' && (
          <div className="brain-overrule">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note"
              aria-label={`Note for ${p.title}`}
            />
            <button className="primary" disabled={pending} onClick={() => onDecide(p.id, 'accept', note)}>
              Accept
            </button>
            <button disabled={pending} onClick={() => onDecide(p.id, 'reject', note)}>
              Reject
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

export default function Brain() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState<BrainDecision | null>(null)
  const [showQuiet, setShowQuiet] = useState(false)
  const [showSectors, setShowSectors] = useState(false)
  const [showEpisodes, setShowEpisodes] = useState(false)
  const [plan, setPlan] = useState({ symbol: '', qty: '' })
  const whatIf = useMutation({
    mutationFn: () => api.brainWhatIf(plan.symbol, Number(plan.qty)),
  })
  const [whySymbol, setWhySymbol] = useState('')

  const latest = useQuery({
    queryKey: ['brainLatest'],
    queryFn: () => api.brainLatestRun('nightly'),
    retry: false,
  })
  const health = useQuery({ queryKey: ['brainHealth'], queryFn: api.brainHealth })
  const episodes = useQuery({ queryKey: ['brainEpisodes'], queryFn: api.brainEpisodes })
  const modules = useQuery({ queryKey: ['brainModules'], queryFn: api.brainModules })
  const runs = useQuery({ queryKey: ['brainRuns'], queryFn: () => api.brainRuns(8) })
  const trace = useQuery({
    queryKey: ['brainTrace', latest.data?.run_id],
    queryFn: () => api.brainRunTrace(latest.data!.run_id),
    enabled: !!latest.data,
  })
  const learning = useQuery({ queryKey: ['brainLearning'], queryFn: () => api.brainLearning() })
  const proposals = useQuery({ queryKey: ['brainProposals'], queryFn: api.brainProposals })

  const refreshAll = useCallback(() => {
    for (const key of ['brainLatest', 'brainHealth', 'brainRuns', 'brainModules', 'brainTrace']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }, [queryClient])
  const runNow = useBrainRunNow(refreshAll)
  const setMode = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: ModuleMode }) => api.setBrainModuleMode(id, mode),
    onSuccess: refreshAll,
  })
  const why = useMutation({ mutationFn: (symbol: string) => api.brainWhy(symbol) })
  const decideProposal = useMutation({
    mutationFn: ({ id, action, note }: { id: number; action: 'accept' | 'reject'; note: string }) =>
      api.brainProposalDecide(id, action, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['brainLearning'] })
      void queryClient.invalidateQueries({ queryKey: ['brainProposals'] })
    },
  })
  const revertBuyLevel = useMutation({
    mutationFn: api.brainRevertBuyLevel,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['brainLearning'] })
      void queryClient.invalidateQueries({ queryKey: ['brainProposals'] })
    },
  })

  const noRunYet = latest.error instanceof ApiError && latest.error.status === 404
  const run = latest.data
  const decisions = run?.decisions ?? []
  const holdings = decisions.filter((d) => d.kind === 'holding')
  const ideas = decisions.filter((d) => d.kind === 'idea')
  const active = ideas.filter((d) => ['TRADE', 'WATCH'].includes(finalWord(d)))
  const quiet = ideas.filter((d) => !['TRADE', 'WATCH'].includes(finalWord(d)))
  const market = run?.banner.mode ? MARKET[run.banner.mode] : null
  const marketSituation = (run?.situations ?? []).find((s) => s.scope === 'market')
  const moduleById = new Map((modules.data?.modules ?? []).map((m) => [m.id, m]))

  return (
    <>
      <div className="page-head">
        <h1>Brain</h1>
        <button className="primary" disabled={runNow.thinking} onClick={() => runNow.start()}>
          {runNow.thinking ? 'Thinking…' : 'Run now'}
        </button>
      </div>
      <p className="muted" style={{ marginTop: '-0.4rem' }}>
        The new decision system, running beside the bot. It only suggests and records — it never places an order.
      </p>
      {runNow.thinking && <p className="muted">{runNowLine(runNow.status, runNow.run?.progress)} This takes a few minutes.</p>}
      {runNow.startError && <ErrorBox error={runNow.startError} />}
      {runNow.run?.status === 'failed' && !runNow.thinking && (
        <p className="neg">The run failed: {runNow.run.error ?? 'unknown error'}</p>
      )}

      {latest.isLoading && <Loading />}
      {latest.isError && !noRunYet && <ErrorBox error={latest.error} />}
      {noRunYet && <Empty label="The brain has not run yet. Press “Run now” to see what it thinks." />}

      {run && market && (
        <div className={`banner ${market.className}`} style={{ marginBottom: '1rem' }}>
          <strong>Market: {market.label}</strong> — {run.banner.headline}
          <div className="stat-sub" style={{ color: 'inherit' }}>
            {market.plain}
          </div>
          {marketSituation && marketSituation.label !== 'unlabelled' && (
            <div className="stat-sub" style={{ color: 'inherit' }}>
              Situation: {marketSituation.label}
              {marketSituation.is_unknown ? ' (unlike any day in its history)' : ''} — {marketSituation.evidence[0]}
            </div>
          )}
        </div>
      )}

      {health.data && (
        <div className="grid brain-health" style={{ marginBottom: '1.25rem' }}>
          <div className="card">
            <div className="stat-sub">Last run</div>
            <strong>{health.data.last_run ? formatDateTime(health.data.last_run.started_at) : 'Never'}</strong>
            {health.data.last_run && (
              <div className="stat-sub">
                {health.data.last_run.status === 'done'
                  ? `Took ${(health.data.last_run.ms / 1000).toFixed(1)} s`
                  : `Failed: ${health.data.last_run.error}`}
              </div>
            )}
          </div>
          <div className="card">
            <div className="stat-sub">Price data</div>
            <strong>
              {health.data.data.fresh == null ? 'Not checked' : health.data.data.fresh ? 'Up to date' : 'Not reliable'}
            </strong>
            <div className="stat-sub">
              {health.data.data.fresh
                ? 'Every check passed.'
                : (health.data.data.issues[0] ?? '') +
                  (health.data.stale_count ? ` · ${health.data.stale_count} stocks affected` : '')}
            </div>
          </div>
          <div className="card">
            <div className="stat-sub">Failed runs, last 7 days</div>
            <strong>{health.data.failed_runs_7d}</strong>
            <div className="stat-sub">
              {health.data.failed_runs_7d ? 'See the run list below.' : 'None — every run finished.'}
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h2>Ask about one stock</h2>
        <form
          className="brain-overrule"
          onSubmit={(e) => {
            e.preventDefault()
            if (whySymbol.trim()) why.mutate(whySymbol)
          }}
        >
          <input
            value={whySymbol}
            onChange={(e) => setWhySymbol(e.target.value)}
            placeholder="Stock symbol, e.g. RELIANCE"
            aria-label="Stock symbol"
          />
          <button className="primary" type="submit" disabled={why.isPending}>
            {why.isPending ? 'Thinking…' : 'Why?'}
          </button>
        </form>
        {why.isError && <ErrorBox error={why.error} />}
        {why.data && !why.data.decision && <Empty label="That stock was not found in the brain's list." />}
      </div>

      {run && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div className="between">
            <h2>Today's ideas</h2>
            <span className="muted">{formatDateTime(run.started_at)}</span>
          </div>
          {active.length === 0 ? (
            <Empty label="Nothing to buy or watch right now." />
          ) : (
            <div className="table-wrap">
              <table>
                <tbody>
                  {active.map((d) => (
                    <DecisionRow key={d.id} d={d} onOpen={() => setOpen(d)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {quiet.length > 0 && (
            <button style={{ marginTop: '0.8rem' }} onClick={() => setShowQuiet((v) => !v)}>
              {showQuiet ? 'Hide' : 'Show'} {quiet.length} stocks with no action
            </button>
          )}
          {showQuiet && (
            <div className="table-wrap" style={{ marginTop: '0.6rem' }}>
              <table>
                <tbody>
                  {quiet.map((d) => (
                    <DecisionRow key={d.id} d={d} onOpen={() => setOpen(d)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {run && holdings.length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h2>Stocks you hold</h2>
          <div className="table-wrap">
            <table>
              <tbody>
                {holdings.map((d) => (
                  <DecisionRow key={d.id} d={d} onOpen={() => setOpen(d)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {run && (run.sectors ?? []).length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div className="between">
            <h2>Sectors</h2>
            <button onClick={() => setShowSectors((v) => !v)}>{showSectors ? 'Hide' : 'Show'}</button>
          </div>
          <p className="stat-sub">
            Which parts of the market are doing better than NIFTY. Stocks in stronger sectors get a small nudge
            when ideas are compared; a sector never decides a trade on its own.
          </p>
          {showSectors && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Sector</th>
                    <th>Direction</th>
                    <th>Last month vs NIFTY</th>
                  </tr>
                </thead>
                <tbody>
                  {run.sectors.map((s) => (
                    <tr key={s.sector}>
                      <td>{s.rank}</td>
                      <td>{s.name}</td>
                      <td>{ROTATION[s.rotation] ?? s.rotation}</td>
                      <td>{s.strength_20d == null ? '—' : `${s.strength_20d >= 0 ? '+' : ''}${(s.strength_20d * 100).toFixed(1)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {run && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h2>Your portfolio as a whole</h2>
          <p className="stat-sub">
            Looking at everything together: what is biggest, and which holdings rise and fall together (owning two of
            those is close to owning one twice).
          </p>
          <ul style={{ margin: '0.4rem 0 0.8rem 1rem' }}>
            <li>
              Biggest position:{' '}
              {run.portfolio?.largest_position
                ? `${run.portfolio.largest_position[0]}, ${(run.portfolio.largest_position[1] * 100).toFixed(0)}% of the portfolio`
                : 'nothing held'}
            </li>
            <li>
              Biggest sector:{' '}
              {run.portfolio?.top_sector
                ? `${run.portfolio.top_sector[0]}, ${(run.portfolio.top_sector[1] * 100).toFixed(0)}% of the portfolio`
                : '—'}
            </li>
            <li>
              Holdings that move together:{' '}
              {(run.portfolio?.holdings_moving_together ?? []).length === 0
                ? 'none'
                : run.portfolio!.holdings_moving_together!.map(([a, b]) => `${a} & ${b}`).join(', ')}
            </li>
          </ul>
          <form
            className="row"
            style={{ gap: '0.5rem', flexWrap: 'wrap' }}
            onSubmit={(e) => {
              e.preventDefault()
              whatIf.mutate()
            }}
          >
            <input
              placeholder="Stock, e.g. TCS"
              value={plan.symbol}
              onChange={(e) => setPlan({ ...plan, symbol: e.target.value })}
            />
            <input
              placeholder="Shares"
              inputMode="numeric"
              value={plan.qty}
              onChange={(e) => setPlan({ ...plan, qty: e.target.value })}
            />
            <button disabled={!plan.symbol || !Number(plan.qty) || whatIf.isPending}>What if I buy this?</button>
          </form>
          {whatIf.isError && <ErrorBox error={whatIf.error} />}
          {whatIf.data && (
            <div className="stat-sub" style={{ marginTop: '0.6rem' }}>
              {whatIf.data.qty} × {whatIf.data.symbol} ≈ {formatCurrency(whatIf.data.value)} → {whatIf.data.symbol} would be{' '}
              {(whatIf.data.stock_share_after * 100).toFixed(0)}% of the portfolio
              {whatIf.data.sector_share_after != null
                ? `, ${whatIf.data.sector_name} ${(whatIf.data.sector_share_after * 100).toFixed(0)}%`
                : ''}
              , cash left {formatCurrency(whatIf.data.cash_after)}.
              {whatIf.data.warnings.length === 0 ? ' Within your limits.' : ''}
              {whatIf.data.warnings.map((w) => (
                <div key={w} style={{ color: 'var(--warn, #b45309)' }}>
                  {w}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(episodes.data ?? []).length > 0 && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <div className="between">
            <h2>Market history</h2>
            <button onClick={() => setShowEpisodes((v) => !v)}>{showEpisodes ? 'Hide' : 'Show'}</button>
          </div>
          <p className="stat-sub">
            The stretches the market went through, as the brain names them. This is the start of its memory.
          </p>
          {showEpisodes && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Situation</th>
                    <th>From</th>
                    <th>To</th>
                    <th>NIFTY change</th>
                  </tr>
                </thead>
                <tbody>
                  {(episodes.data ?? []).map((e) => (
                    <tr key={e.start_day}>
                      <td>{e.label}</td>
                      <td>{e.start_day}</td>
                      <td>{e.end_day ?? 'still going'}</td>
                      <td>
                        {e.nifty_change == null ? '—' : `${e.nifty_change >= 0 ? '+' : ''}${(e.nifty_change * 100).toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h2>Learning from results</h2>
        <p className="stat-sub">
          How well the brain's past ideas actually worked out, and any changes it wants to make. Nothing changes
          until you press Accept.
        </p>
        {learning.isLoading && <Loading />}
        {learning.isError && <ErrorBox error={learning.error} />}
        {learning.data && (
          <>
            {learning.data.note && (
              <div className="banner banner-info" style={{ margin: '0.6rem 0' }}>
                {learning.data.note}
              </div>
            )}

            {learning.data.by_band.length > 0 && (
              <>
                <h3>Model score (a ranking, not a chance)</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Model score</th>
                        <th>Ideas</th>
                        <th>The brain said</th>
                        <th>What happened</th>
                        <th>Average result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learning.data.by_band.map((b) => (
                        <tr key={b.band}>
                          <td>{b.band}</td>
                          <td>{b.n}</td>
                          <td>{(b.said * 100).toFixed(0)}%</td>
                          <td>{(b.hit * 100).toFixed(0)}%</td>
                          <td>
                            {b.avg_r >= 0 ? '+' : ''}
                            {b.avg_r.toFixed(2)} R
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {learning.data.by_word.length > 0 && (
              <>
                <h3>By decision</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Decision</th>
                        <th>Ideas</th>
                        <th>What happened</th>
                        <th>Average result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learning.data.by_word.map((w) => (
                        <tr key={w.word}>
                          <td>
                            <WordPill word={w.word} />
                          </td>
                          <td>{w.n}</td>
                          <td>{(w.hit * 100).toFixed(0)}%</td>
                          <td>
                            {w.avg_r >= 0 ? '+' : ''}
                            {w.avg_r.toFixed(2)} R
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {learning.data.by_week.length > 0 && (
              <>
                <h3>By week</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Ideas</th>
                        <th>What happened</th>
                        <th>Average result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learning.data.by_week.map((w) => (
                        <tr key={w.week}>
                          <td>{w.week}</td>
                          <td>{w.n}</td>
                          <td>{(w.hit * 100).toFixed(0)}%</td>
                          <td>
                            {w.avg_r >= 0 ? '+' : ''}
                            {w.avg_r.toFixed(2)} R
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {(learning.data.by_band.length > 0 ||
              learning.data.by_word.length > 0 ||
              learning.data.by_week.length > 0) && (
              <p className="stat-sub">(1 R = the 4% risked on each trade)</p>
            )}

            {learning.data.failures.length > 0 && (
              <>
                <h3>What tends to go wrong</h3>
                <ul className="brain-reasons">
                  {learning.data.failures.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </>
            )}

            {learning.data.drift_lines.length > 0 ? (
              <>
                <h3>Has the market changed under the model?</h3>
                <ul className="brain-reasons">
                  {learning.data.drift_lines.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              </>
            ) : (
              learning.data.drift_note && <p className="stat-sub">{learning.data.drift_note}</p>
            )}
          </>
        )}

        <h3>Proposals</h3>
        <p className="stat-sub">
          Changes the brain wants to make, based on the results above. Nothing changes until you press Accept.
        </p>
        <p className="stat-sub">
          Accepting a buy level changes what the brain suggests from its next nightly run; it does not change
          how the existing bot trades.
        </p>
        {proposals.data && currentBuyLevelProposal(proposals.data) && (
          <button disabled={revertBuyLevel.isPending} onClick={() => revertBuyLevel.mutate()}>
            Go back to the default buy level
          </button>
        )}
        {revertBuyLevel.isError && <ErrorBox error={revertBuyLevel.error} />}
        {proposals.isLoading && <Loading />}
        {proposals.isError && <ErrorBox error={proposals.error} />}
        {proposals.data && proposals.data.length === 0 && <Empty label="No proposals right now." />}
        {proposals.data && proposals.data.length > 0 && (
          <div className="table-wrap">
            <table>
              <tbody>
                {proposals.data.map((p) => (
                  <ProposalRow
                    key={p.id}
                    p={p}
                    pending={decideProposal.isPending}
                    onDecide={(id, action, note) => decideProposal.mutate({ id, action, note })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {decideProposal.isError && <ErrorBox error={decideProposal.error} />}
      </div>

      <BrainCompareCard />
      <BrainStageCard />
      <BrainApprovalsCard />

      {modules.data && (
        <div className="card" style={{ marginBottom: '1.25rem' }}>
          <h2>The eight thinking steps</h2>
          <p className="stat-sub">
            Each step is answered by an installed module, or by a simple built-in answer until one is built. “Trial”
            runs a module and records its answer without using it.
          </p>
          <div className="table-wrap">
            <table>
              <tbody>
                {modules.data.steps.map((s) => (
                  <tr key={s.step}>
                    <td>
                      <strong>{STEP_LABEL[s.step] ?? s.step}</strong>
                    </td>
                    <td>
                      {s.modules.length === 0 && <span className="muted">Simple built-in answer</span>}
                      {s.modules.map((id) => {
                        const m = moduleById.get(id)
                        return m ? (
                          <div key={id} className="between brain-module">
                            <span title={`${m.id} · ${m.name}`}>
                              <strong>{moduleName(m.id)}</strong>
                              <span className="muted"> — {MODULE_PLAIN[m.id]?.does}</span>
                            </span>
                            <ModuleSwitch m={m} onChange={(mode) => setMode.mutate({ id: m.id, mode })} />
                          </div>
                        ) : null
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {setMode.isError && <ErrorBox error={setMode.error} />}
        </div>
      )}

      {run && run.live && <AlertCard runId={run.run_id} />}

      {runs.data && runs.data.length > 0 && (
        <div className="card">
          <h2>Recent runs</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Market</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.map((r) => (
                  <tr key={r.run_id}>
                    <td>
                      {formatDateTime(r.started_at)}
                      <div className="stat-sub">
                        {r.kind === 'why' ? 'One stock' : r.kind === 'intraday' ? 'Holdings check' : 'Nightly'}
                        {!r.live && ' · replay'}
                      </div>
                    </td>
                    <td>{r.banner.mode ? MARKET[r.banner.mode].label : '—'}</td>
                    <td>
                      {r.status === 'failed' ? (
                        <span className="badge badge-off">Failed</span>
                      ) : (
                        Object.entries(r.counts)
                          .map(([w, n]) => `${n} ${w}`)
                          .join(' · ')
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open && (
        <Modal onClose={() => setOpen(null)}>
          <DecisionDetail
            decision={open}
            trace={trace.data?.trace}
            onOverruled={() => {
              setOpen(null)
              refreshAll()
            }}
          />
        </Modal>
      )}
      {why.data?.decision && (
        <Modal onClose={() => why.reset()}>
          <DecisionDetail decision={why.data.decision} trace={why.data.trace} onOverruled={() => why.reset()} />
        </Modal>
      )}
    </>
  )
}
